import React from 'react';
import { Clock, Users, CreditCard, Calendar } from 'lucide-react';
import { Member, Contribution, Event, Town } from '../../types';
import { formatCurrency } from '../../utils/currency';

interface RecentActivityProps {
  members: Member[];
  contributions: Contribution[];
  events: Event[];
  towns: Town[];
  selectedTownId: string;
}

export const RecentActivity: React.FC<RecentActivityProps> = ({
  members,
  contributions,
  events,
  towns,
  selectedTownId
}) => {
  const filteredMembers = selectedTownId === 'all' 
    ? members 
    : members.filter(m => m.townId === selectedTownId);
  
  const filteredContributions = selectedTownId === 'all'
    ? contributions
    : contributions.filter(c => c.townId === selectedTownId);

  const filteredEvents = selectedTownId === 'all'
    ? events
    : events.filter(e => e.townId === selectedTownId);

  const recentMembers = filteredMembers
    .sort((a, b) => new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime())
    .slice(0, 3);

  const recentContributions = filteredContributions
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  const upcomingEvents = filteredEvents
    .filter(e => new Date(e.date) >= new Date())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 3);

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border dark:border-dark-border-primary p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4 flex items-center">
        <Clock className="h-5 w-5 mr-2" />
        Recent Activity
      </h2>

      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3 flex items-center">
            <Users className="h-4 w-4 mr-2" />
            New Members
          </h3>
          <div className="space-y-2">
            {recentMembers.map((member) => {
              const town = towns.find(t => t.id === member.townId);
              return (
                <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <Users className="h-3 w-3 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{member.name}</p>
                      <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">{member.username} • {town?.name}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-dark-text-tertiary">{member.registrationDate}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3 flex items-center">
            <CreditCard className="h-4 w-4 mr-2" />
            Recent Contributions
          </h3>
          <div className="space-y-2">
            {recentContributions.map((contribution) => {
              const member = members.find(m => m.id === contribution.memberId);
              const town = towns.find(t => t.id === contribution.townId);
              return (
                <div key={contribution.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-full">
                      <CreditCard className="h-3 w-3 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{member?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">{contribution.type} • {town?.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{formatCurrency(contribution.amount)}</p>
                    <p className="text-xs text-gray-400 dark:text-dark-text-tertiary">{contribution.date}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3 flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            Upcoming Events
          </h3>
          <div className="space-y-2">
            {upcomingEvents.map((event) => {
              const town = towns.find(t => t.id === event.townId);
              return (
                <div key={event.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-purple-100 p-2 rounded-full">
                      <Calendar className="h-3 w-3 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{event.title}</p>
                      <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">{event.type} • {town?.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{event.date}</p>
                    <p className="text-xs text-gray-400 dark:text-dark-text-tertiary">{event.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};